// Behind The Broadcast — theme behaviour (ported from the static build)
// Main JavaScript for Broadcast Website

// Initialize navigation header actions and effects
const initializeHeader = () => {
  const mainHeader = document.getElementById('main-header');
  if (!mainHeader) return;
  if (mainHeader.dataset.headerInit === 'true') return;
  mainHeader.dataset.headerInit = 'true';
  
  // Sticky Header Scroll Effect
  const handleScroll = () => {
    if (window.scrollY > 10) {
      mainHeader.classList.add('scrolled');
    } else {
      mainHeader.classList.remove('scrolled');
    }
  };
  window.addEventListener('scroll', handleScroll);
  handleScroll(); // Trigger immediately in case page is loaded mid-scroll

  // Mobile Drawer Toggle
  const mobileNavToggle = document.getElementById('mobile-nav-toggle');
  const mobileDrawer = document.getElementById('mobile-drawer');
  const drawerClose = document.getElementById('drawer-close');
  const drawerOverlay = document.getElementById('drawer-overlay');

  const openDrawer = () => {
    mobileDrawer.classList.add('active');
    drawerOverlay.classList.add('active');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeDrawer = () => {
    mobileDrawer.classList.remove('active');
    drawerOverlay.classList.remove('active');
    document.body.style.overflow = ''; // Restore background scrolling
  };

  if (mobileNavToggle) {
    mobileNavToggle.addEventListener('click', openDrawer);
  }

  if (drawerClose) {
    drawerClose.addEventListener('click', closeDrawer);
  }

  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', closeDrawer);
  }

  // Mobile Accordion Submenu Toggles
  const drawerLinks = document.querySelectorAll('.drawer-link, .drawer-arrow-btn');

  drawerLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      // If it's a real link, let it proceed to navigate
      const href = link.getAttribute('href');
      if (href && href !== '#') {
        return; 
      }

      e.preventDefault();
      e.stopPropagation(); // Avoid triggering parent clicks
      
      const parentItem = link.closest('.drawer-item');
      if (!parentItem) return;
      const submenu = parentItem.querySelector('.drawer-submenu');
      const arrowBtn = parentItem.querySelector('.drawer-arrow-btn');
      
      // Toggle current submenu
      if (submenu) {
        const isOpen = submenu.classList.contains('open');
        
        // Close all other submenus first (Accordion behavior)
        document.querySelectorAll('.drawer-submenu').forEach(sub => {
          sub.classList.remove('open');
        });
        document.querySelectorAll('.drawer-arrow-btn').forEach(arrow => {
          arrow.classList.remove('open');
        });

        // If it wasn't open, open it
        if (!isOpen) {
          submenu.classList.add('open');
          if (arrowBtn) arrowBtn.classList.add('open');
        }
      }
    });
  });
};

// Infinite scroll implementation for mobile carousel
const initInfiniteScroll = (selector) => {
  const container = document.querySelector(selector);
  if (!container) return;

  const items = Array.from(container.children);
  if (items.length === 0) return;

  // Clone original columns to create infinite loop visual buffer
  items.forEach(item => {
    const clone = item.cloneNode(true);
    clone.classList.add('carousel-clone');
    container.appendChild(clone);
  });
  
  items.forEach(item => {
    const clone = item.cloneNode(true);
    clone.classList.add('carousel-clone');
    container.insertBefore(clone, container.firstChild);
  });

  // Calculate layout offset based on visible images
  const getScrollOffset = () => {
    const images = Array.from(container.querySelectorAll('img:not(.carousel-clone img)'));
    if (images.length === 0) return 0;
    
    // Each image has width 280px + 15px gap = 295px on mobile
    const firstImg = images[0];
    const rect = firstImg.getBoundingClientRect();
    // Fallback to 280 if not rendered yet
    const imgWidth = rect.width > 0 ? rect.width : 280;
    const gap = parseInt(window.getComputedStyle(container).gap || 15);
    
    return (imgWidth + gap) * images.length;
  };

  const resetScrollPosition = () => {
    if (window.innerWidth > 768) return; // Only scroll-reposition on mobile
    const totalWidth = getScrollOffset();
    
    if (container.scrollLeft <= 5) {
      container.scrollLeft = totalWidth;
    } else if (container.scrollLeft >= totalWidth * 2 - 5) {
      container.scrollLeft = totalWidth;
    }
  };

  // Wait for content load to center scroll position
  const runInit = () => {
    if (window.innerWidth <= 768) {
      const totalWidth = getScrollOffset();
      container.scrollLeft = totalWidth;
    }
  };

  window.addEventListener('load', runInit);
  setTimeout(runInit, 200); // safety fallback

  // Seamless jump scroll listener
  container.addEventListener('scroll', () => {
    if (window.innerWidth > 768) return;
    const totalWidth = getScrollOffset();
    if (totalWidth === 0) return;

    if (container.scrollLeft <= 5) {
      // jumped to start clones -> reset to center original
      container.scrollLeft = totalWidth;
    } else if (container.scrollLeft >= totalWidth * 2 - 10) {
      // jumped to end clones -> reset to center original
      container.scrollLeft = totalWidth;
    }
  });

  window.addEventListener('resize', runInit);
};

// Comprehensive Scroll Reveal & Animation Controller
const initScrollReveal = () => {
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -40px 0px',
    threshold: 0.08 // Trigger smoothly when 8% is visible
  };

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        entry.target.classList.add('group-visible');
        
        // Trigger number counters inside this container if any
        animateCounters(entry.target);
        
        obs.unobserve(entry.target); // Animate once cleanly
      }
    });
  }, observerOptions);

  // Auto-detect and observe all core sections, cards, and reveal targets
  const selectors = [
    '.reveal',
    '.reveal-on-scroll',
    '.reveal-up',
    '.reveal-down',
    '.reveal-left',
    '.reveal-right',
    '.reveal-scale',
    '.reveal-fade',
    '.stagger-group',
    '.hero-content',
    '.hero-stats-box',
    '.explore-section',
    '.explore-card',
    '.trusted-section',
    '.collections-section',
    '.about-section',
    '.podcast-section',
    '.newsletter-section',
    '.partnerships-section',
    '.all-in-one-section',
    '.essentials-section',
    '.testimonials-section',
    '.instagram-section',
    '.story-section',
    '.network-section',
    '.guide-section',
    '.gallery-section',
    '.youtube-page-header',
    '.contact-hero-content',
    '.contact-form-col',
    '.contact-details-container',
    '.why-partner-heading',
    '.partner-benefit-item',
    '.behind-scenes-header',
    '.behind-card',
    '.shop-heading-wrap',
    '.shop-tabs-nav',
    '.shop-card',
    '.error-inner',
    '.ty-inner',
    '.privacy-container'
  ];

  const elements = document.querySelectorAll(selectors.join(', '));
  
  elements.forEach(el => {
    // Never apply scroll reveal animation to individual carousel slider cards
    if (el.closest('.products-grid, #recProductsGrid, .recommendations-carousel, .reviews-grid-col, .youtube-page-grid, #youtubePageGrid, .youtube-grid, #youtubeHomeGrid') || el.classList.contains('youtube-page-card') || el.classList.contains('youtube-card')) {
      el.classList.remove('reveal-on-scroll', 'reveal-up', 'reveal', 'reveal-left', 'reveal-right', 'reveal-fade', 'reveal-scale');
      el.classList.add('is-visible');
      el.style.transform = 'none';
      return;
    }

    if (!el.classList.contains('reveal') && 
        !el.classList.contains('reveal-on-scroll') && 
        !el.classList.contains('reveal-up') &&
        !el.classList.contains('reveal-left') &&
        !el.classList.contains('reveal-right') &&
        !el.classList.contains('reveal-scale') &&
        !el.classList.contains('stagger-group')) {
      el.classList.add('reveal-on-scroll');
    }
    observer.observe(el);
  });

  // Auto-apply staggered transition delays for static grid items (excluding horizontal carousels)
  const gridContainers = document.querySelectorAll(
    '.shop-grid, .behind-scenes-grid, .explore-grid, .partner-benefits, .features-list-grid, .gallery-grid-2x2, .collections-grid'
  );

  gridContainers.forEach(grid => {
    const children = Array.from(grid.children);
    children.forEach((child, index) => {
      if (!child.style.transitionDelay && !child.classList.contains('stagger-1')) {
        const delay = Math.min((index % 6) * 0.08, 0.48);
        child.style.transitionDelay = `${delay}s`;
      }
      if (!child.classList.contains('reveal-on-scroll') && !child.classList.contains('reveal')) {
        child.classList.add('reveal-on-scroll');
      }
      observer.observe(child);
    });
  });

  // Ensure all horizontal YouTube carousel cards never have reveal animations
  document.querySelectorAll('.youtube-page-card, .youtube-card').forEach(card => {
    card.classList.remove('reveal', 'reveal-on-scroll', 'reveal-up', 'reveal-fade', 'reveal-scale');
    card.classList.add('is-visible');
    card.style.transitionDelay = '0s';
  });

  // Immediate check for hero elements on initial viewport load
  setTimeout(() => {
    const aboveFold = document.querySelectorAll('.hero-content, .hero-stats-box, .contact-hero-content, .error-inner, .ty-inner');
    aboveFold.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        el.classList.add('is-visible');
        animateCounters(el);
      }
    });
  }, 50);
};

// Dynamic Smooth Number Counter Animation with easeOutExpo
const animateCounters = (container) => {
  // Only animate elements that opted in. The static build matched every
  // .stats-value, which made "Since 2021" count up from zero.
  const counterElements = container.querySelectorAll('[data-counter]');
  
  counterElements.forEach(el => {
    if (el.dataset.animated === 'true') return;
    el.dataset.animated = 'true';

    const originalText = el.innerText.trim();
    
    // Check if format is e.g. "376K+", "4.9", "2021", "50+"
    let match = originalText.match(/^([^\d]*)(\d+(?:\.\d+)?)(.*)$/);
    if (!match) return;

    const prefix = match[1] || '';
    const targetVal = parseFloat(match[2]);
    const suffix = match[3] || '';
    const isDecimal = match[2].includes('.');
    const decimalPlaces = isDecimal ? match[2].split('.')[1].length : 0;

    let startTime = null;
    const duration = 1600; // ms

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutExpo formula: progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentVal = targetVal * easeOut;

      el.innerText = prefix + (isDecimal ? currentVal.toFixed(decimalPlaces) : Math.floor(currentVal)) + suffix;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.innerText = originalText; // Ensure exact original string preserved
      }
    };

    window.requestAnimationFrame(step);
  });
};

// Reading Scroll Progress Bar for Policy & Article pages
const initScrollProgress = () => {
  if (document.querySelector('.scroll-progress-bar')) return;
  
  // Only add if page is long or policy page
  if (document.body.scrollHeight > window.innerHeight * 1.5) {
    const bar = document.createElement('div');
    bar.className = 'scroll-progress-bar';
    document.body.appendChild(bar);

    const updateProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight > 0) {
        const percent = Math.min((scrollTop / docHeight) * 100, 100);
        bar.style.width = `${percent}%`;
      }
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }
};

// Micro-interactions for Product Pages & Buttons
const initMicroInteractions = () => {
  // Quantity buttons bounce feedback
  const qtyBtns = document.querySelectorAll('.qty-btn, .quantity-selector-box button');
  qtyBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.parentElement ? btn.parentElement.querySelector('input') : null;
      if (input) {
        input.style.transform = 'scale(1.2)';
        input.style.transition = 'transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        setTimeout(() => {
          input.style.transform = 'scale(1)';
        }, 150);
      }
    });
  });

  // Buttons ripple & magnetic click feel
  const allActionBtns = document.querySelectorAll('.btn-hero-primary, .btn-explore, .btn-submit-inquiry, .btn-submit-message, .btn-product-add-to-cart, .btn-product-buy-now, .shop-card-cart-btn, .btn-product-cart');
  allActionBtns.forEach(btn => {
    btn.addEventListener('mousedown', () => {
      btn.style.transform = 'scale(0.95)';
    });
    btn.addEventListener('mouseup', () => {
      btn.style.transform = '';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
    });
  });
};

// Initialize Testimonials Slider and Sync Dots
const initTestimonialsSlider = () => {
  const track = document.querySelector('.testimonials-track');
  const dots = document.querySelectorAll('.testimonials-dots .dot');
  if (!track || dots.length === 0) return;

  const updateActiveDot = () => {
    const scrollLeft = track.scrollLeft;
    const cards = track.querySelectorAll('.testimonial-card');
    if (cards.length === 0) return;

    const cardWidth = cards[0].offsetWidth;
    const style = window.getComputedStyle(track);
    const gap = parseInt(style.gap) || 25;
    const step = cardWidth + gap;

    // Calculate index of closest card
    const activeIndex = Math.round(scrollLeft / step);

    dots.forEach((dot, idx) => {
      if (idx === activeIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  };

  // Add scroll listener to update dots when user scrolls or swipes
  track.addEventListener('scroll', updateActiveDot);

  // Add click listener to dots for navigation
  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      const cards = track.querySelectorAll('.testimonial-card');
      if (cards.length === 0) return;
      const cardWidth = cards[0].offsetWidth;
      const style = window.getComputedStyle(track);
      const gap = parseInt(style.gap) || 25;
      const targetScroll = idx * (cardWidth + gap);

      track.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    });
  });

  // Drag to scroll functionality (mouse slide)
  let isDown = false;
  let startX;
  let scrollLeftVal;

  const snapToNearestCard = () => {
    const cards = track.querySelectorAll('.testimonial-card');
    if (cards.length === 0) return;
    const cardWidth = cards[0].offsetWidth;
    const style = window.getComputedStyle(track);
    const gap = parseInt(style.gap) || 25;
    const step = cardWidth + gap;

    const targetIndex = Math.round(track.scrollLeft / step);
    const targetScroll = targetIndex * step;

    track.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  track.addEventListener('mousedown', (e) => {
    isDown = true;
    track.classList.add('dragging');
    track.style.scrollSnapType = 'none'; // Temporarily disable snapping during drag
    track.style.scrollBehavior = 'auto'; // Remove smooth scroll for instant drag movement
    startX = e.pageX - track.offsetLeft;
    scrollLeftVal = track.scrollLeft;
  });

  const handleDragEnd = () => {
    if (!isDown) return;
    isDown = false;
    track.classList.remove('dragging');
    track.style.scrollSnapType = 'x mandatory'; // Restore snapping
    track.style.scrollBehavior = 'smooth';
    snapToNearestCard();
  };

  track.addEventListener('mouseleave', handleDragEnd);
  track.addEventListener('mouseup', handleDragEnd);

  track.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - track.offsetLeft;
    const walk = (x - startX) * 1.5; // Drag speed multiplier
    track.scrollLeft = scrollLeftVal - walk;
  });

  // Initial run
  updateActiveDot();
};

// Initialize Network Carousel Controls
const initNetworkCarousel = () => {
  const wrapper = document.querySelector('.network-images-wrapper');
  const prevBtn = document.querySelector('.btn-carousel-prev');
  const nextBtn = document.querySelector('.btn-carousel-next');
  const cols = document.querySelectorAll('.network-img-col');
  if (!wrapper || !prevBtn || !nextBtn || cols.length === 0) return;

  let activeIndex = 0;

  const updateCarousel = () => {
    // Scroll step is 328px (width of col) + 24px (gap) = 352px
    const scrollAmount = activeIndex * 352;
    wrapper.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    
    cols.forEach((c, idx) => {
      if (idx === activeIndex) {
        c.classList.add('active');
      } else {
        c.classList.remove('active');
      }
    });
  };

  prevBtn.addEventListener('click', () => {
    if (activeIndex > 0) {
      activeIndex--;
      updateCarousel();
    }
  });

  nextBtn.addEventListener('click', () => {
    if (activeIndex < cols.length - 1) {
      activeIndex++;
      updateCarousel();
    }
  });
};

// Initialize About Section Images Slider
const initAboutSlider = () => {
  const track     = document.querySelector('.about-slider-track');
  const container = document.querySelector('.about-slider-container');
  const prevBtn   = document.querySelector('.btn-slider-prev');
  const nextBtn   = document.querySelector('.btn-slider-next');
  if (!track || !container || !prevBtn || !nextBtn) return;

  const imgs       = track.querySelectorAll('.about-slider-img');
  const total      = imgs.length;
  let currentIndex = 0;

  const isMobile = () => window.innerWidth <= 768;

  /* ── DESKTOP helpers ── */
  const desktopUpdate = () => {
    const step     = 340; // 320px image + 20px gap
    const maxIndex = total - 2;
    track.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
    track.style.transform  = `translateX(-${currentIndex * step}px)`;
    prevBtn.disabled = currentIndex <= 0;
    nextBtn.disabled = currentIndex >= maxIndex;
    prevBtn.classList.toggle('active', currentIndex > 0);
    nextBtn.classList.toggle('active', currentIndex < maxIndex);
  };

  /* ── MOBILE helpers ── */
  const mobileUpdate = () => {
    // Never use transform on mobile – CSS scroll handles position
    track.style.transform  = 'none';
    track.style.transition = 'none';
    const atStart = container.scrollLeft <= 4;
    const atEnd   = container.scrollLeft >= (container.scrollWidth - container.clientWidth - 4);
    prevBtn.disabled = atStart;
    nextBtn.disabled = atEnd;
    prevBtn.classList.toggle('active', !atStart);
    nextBtn.classList.toggle('active', !atEnd);
  };

  const update = () => isMobile() ? mobileUpdate() : desktopUpdate();

  /* ── Events ── */
  container.addEventListener('scroll', () => { if (isMobile()) mobileUpdate(); }, { passive: true });

  prevBtn.addEventListener('click', () => {
    if (isMobile()) {
      // scroll left by one image width + gap
      const imgW = imgs[0] ? imgs[0].getBoundingClientRect().width + 14 : container.clientWidth;
      container.scrollBy({ left: -imgW, behavior: 'smooth' });
    } else if (currentIndex > 0) {
      currentIndex--;
      desktopUpdate();
    }
  });

  nextBtn.addEventListener('click', () => {
    if (isMobile()) {
      const imgW = imgs[0] ? imgs[0].getBoundingClientRect().width + 14 : container.clientWidth;
      container.scrollBy({ left: imgW, behavior: 'smooth' });
    } else if (currentIndex < total - 2) {
      currentIndex++;
      desktopUpdate();
    }
  });

  window.addEventListener('resize', () => {
    if (!isMobile()) {
      if (currentIndex > total - 2) currentIndex = total - 2;
    } else {
      track.style.transform = 'none';
    }
    update();
  });

  // Initial state
  update();
};

// Initialize Essentials Products Grid — native scroll only (no drag, no wheel hijack)
const initProductsCarousel = () => {
  // Nothing to do — CSS handles scroll-snap and overflow-x: auto.
  // Native touch swipe (mobile) and trackpad/mouse scroll (desktop) work naturally.
};

const initBtbTheme = () => {
  // Header & footer are rendered server-side by Liquid sections.
  initializeHeader();
  
  // Initialize About Images Slider
  initAboutSlider();

  // Initialize Testimonials Slider
  initTestimonialsSlider();

  // Initialize Network Carousel
  initNetworkCarousel();

  // Initialize Products Grid Carousel
  initProductsCarousel();

  // Initialize Scroll Reveal Animations
  initScrollReveal();

  // Initialize Reading Scroll Progress Bar
  initScrollProgress();

  // Initialize Button & Input Micro-Interactions
  initMicroInteractions();
};

document.addEventListener('DOMContentLoaded', initBtbTheme);
// Re-run when the theme editor re-renders a section so behaviour survives edits.
document.addEventListener('shopify:section:load', initBtbTheme);
window.initBtbTheme = initBtbTheme;

window.changeCardColor = function(event, swatchEl, newImgSrc) {
  if (event) {
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    if (typeof event.preventDefault === 'function') event.preventDefault();
  }
  const card = swatchEl.closest('.product-card, .shop-card');
  if (!card) return;

  const imgEl = card.querySelector('.product-img, .shop-card-img');
  if (imgEl && newImgSrc) {
    imgEl.src = newImgSrc;
    if (imgEl.hasAttribute('srcset')) imgEl.removeAttribute('srcset');
  }

  // Keep the card's links pointing at the matching variant so the PDP opens on that colour.
  const variantId = swatchEl.getAttribute('data-variant-id');
  if (variantId) {
    card.querySelectorAll('a[data-product-url]').forEach(a => {
      a.setAttribute('href', a.getAttribute('data-product-url') + '?variant=' + variantId);
    });
  }

  const swatches = card.querySelectorAll('.card-swatch');
  swatches.forEach(s => s.classList.remove('active'));
  swatchEl.classList.add('active');
};

// Global Handler: Click on "+X colors" navigates directly to product page
window.openColorModal = function(event, countEl) {
  if (event) {
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    if (typeof event.preventDefault === 'function') event.preventDefault();
  }
  const card = countEl ? countEl.closest('.product-card, .shop-card') : null;
  if (!card) return;

  const linkEl = card.querySelector('a[data-product-url], a[href]');
  if (linkEl && linkEl.getAttribute('href')) {
    window.location.href = linkEl.getAttribute('href');
  }
};


// Universal Product Detail Accordion Handler (macOS/Safari/iOS/Android/Windows compatible)
window.toggleAccordion = function(btnOrEvent) {
  let btn = btnOrEvent;
  if (btnOrEvent && btnOrEvent.currentTarget) btn = btnOrEvent.currentTarget;
  else if (btnOrEvent && btnOrEvent.target) btn = btnOrEvent.target.closest('.accordion-header') || btnOrEvent.target;
  if (!btn) return;
  const item = btn.closest ? btn.closest('.accordion-item') : null;
  if (!item) return;
  const isOpen = item.classList.contains('active');
  if (isOpen) {
    item.classList.remove('active');
    btn.setAttribute('aria-expanded', 'false');
  } else {
    item.classList.add('active');
    btn.setAttribute('aria-expanded', 'true');
  }
};

// Global Function: Automatically select color on PDP if ?color=... parameter is present in URL
function handleUrlColorParam() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const colorParam = urlParams.get('color');
    if (!colorParam) return;

    const normalized = colorParam.trim().toLowerCase();
    
    // Check PDP color swatches
    const swatches = Array.from(document.querySelectorAll('.pdp-color-swatch, .color-swatch-btn, [data-color]'));
    const matched = swatches.find(el => {
      const c = (el.getAttribute('data-color') || el.getAttribute('title') || el.getAttribute('aria-label') || '').trim().toLowerCase();
      return c === normalized;
    });

    if (matched) {
      matched.click();
      return;
    }

    // Check PDP thumbnail carousel items
    const thumbs = Array.from(document.querySelectorAll('.pdp-carousel-thumb'));
    const matchedThumb = thumbs.find(t => (t.getAttribute('data-color') || '').trim().toLowerCase() === normalized);
    if (matchedThumb) {
      matchedThumb.click();
    }
  } catch (err) {
    console.error('Error handling URL color param:', err);
  }
}

// Run once the variant picker has bound its listeners (product.js boots on the
// same DOMContentLoaded tick, so always defer by a frame).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { setTimeout(handleUrlColorParam, 50); });
} else {
  setTimeout(handleUrlColorParam, 50);
}

// Global Event Delegation:
// 1. Click color swatch -> Change card preview image to that color
// 2. Click "+X colors" -> Navigate to product page
document.addEventListener('click', function(e) {
  // 1. Color Swatch Click -> Change preview image
  const swatch = e.target.closest('.card-swatch, .swatch');
  if (swatch) {
    e.preventDefault();
    e.stopPropagation();

    const card = swatch.closest('.product-card, .shop-card');
    if (!card) return;

    let imgSrc = swatch.getAttribute('data-img') || swatch.getAttribute('data-image') || '';

    // In Shopify every swatch carries its variant image via data-img, so the
    // per-product lookup tables the static build needed are gone.
    if (imgSrc) {
      window.changeCardColor(e, swatch, imgSrc);
    }
    return;
  }

  // 2. Click "+X colors" -> Navigate to product page
  const countBtn = e.target.closest('.card-swatch-count, .product-swatches-count, .swatch-more');
  if (countBtn) {
    e.preventDefault();
    e.stopPropagation();

    const card = countBtn.closest('.product-card, .shop-card');
    let targetHref = countBtn.getAttribute('href');

    if (!targetHref && card) {
      const linkEl = card.querySelector('a[data-product-url], a[href]');
      if (linkEl) targetHref = linkEl.getAttribute('href');
    }

    if (targetHref) {
      window.location.href = targetHref;
    }
    return;
  }
}, true);


// Universal PDP Thumbnail Track Wheel & Mouse Drag Scroll
function initPdpGalleryCarousel() {
  const tracks = document.querySelectorAll('.pdp-carousel-track, #pdpTrack');
  tracks.forEach(track => {
    if (track.dataset.wheelInit) return;
    track.dataset.wheelInit = 'true';

    // 1. Mouse wheel horizontal scrolling
    track.addEventListener('wheel', function(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) || e.deltaY !== 0) {
        e.preventDefault();
        track.scrollLeft += e.deltaY > 0 ? 100 : -100;
      }
    }, { passive: false });

    // 2. Mouse drag-to-scroll on desktop
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasMoved = false;

    track.style.cursor = 'grab';

    track.addEventListener('mousedown', function(e) {
      isDown = true;
      hasMoved = false;
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });

    window.addEventListener('mouseup', function() {
      if (isDown) {
        isDown = false;
        track.style.cursor = 'grab';
        track.style.userSelect = '';
      }
    });

    window.addEventListener('mousemove', function(e) {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 4) hasMoved = true;
      track.scrollLeft = scrollLeft - walk;
    });

    // Prevent accidental thumbnail click if user was dragging
    track.addEventListener('click', function(e) {
      if (hasMoved) {
        e.stopPropagation();
        hasMoved = false;
      }
    }, true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpGalleryCarousel);
} else {
  initPdpGalleryCarousel();
}
window.initPdpGalleryCarousel = initPdpGalleryCarousel;



/* ==========================================================================
   Shop Pay "Learn More" Interactive Modal Handler
   ========================================================================== */
function initShopPayModal() {
  function createShopPayModal() {
    if (document.getElementById('shoppayModalOverlay')) return;

    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'shoppay-modal-overlay';
    modalOverlay.id = 'shoppayModalOverlay';
    modalOverlay.innerHTML = `
      <div class="shoppay-modal-card" role="dialog" aria-modal="true" aria-labelledby="shoppayModalTitle">
        <!-- Header -->
        <div class="shoppay-modal-header">
          <div class="shoppay-header-brand">
            <span class="shoppay-badge-icon">shop</span>
            <h3 class="shoppay-modal-title" id="shoppayModalTitle">Get it now, pay later</h3>
          </div>
          <button type="button" class="shoppay-modal-close" id="shoppayModalClose" aria-label="Close modal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="shoppay-modal-body">
          <p class="shoppay-modal-intro">For orders over $35.00, select installments at checkout to split your purchase over time.</p>

          <div class="shoppay-benefits-list">
            <div class="shoppay-benefit-item">
              <div class="shoppay-benefit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#080A0D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v12M15 9.5a2.5 2.5 0 0 0-2.5-2.5h-1a2.5 2.5 0 0 0 0 5h1a2.5 2.5 0 0 1 0 5h-1A2.5 2.5 0 0 1 9 14.5"></path>
                </svg>
              </div>
              <span class="shoppay-benefit-text">No fees, ever.</span>
            </div>

            <div class="shoppay-benefit-item">
              <div class="shoppay-benefit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#080A0D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
              </div>
              <span class="shoppay-benefit-text">No impact on your credit score to apply.</span>
            </div>
          </div>

          <p class="shoppay-modal-disclaimer">
            Payment options are offered by Affirm and are subject to an eligibility check and might not be available in all states. CA Residents: Loans by Affirm Loan Services, LLC are made or arranged pursuant to a California Finance Lender license.
          </p>

          <!-- Footer Brands -->
          <div class="shoppay-modal-footer">
            <div class="shoppay-footer-logo-shop">shop</div>
            <div class="shoppay-footer-partner">
              <span>Installments in partnership with</span>
              <span class="affirm-brand-wrap">
                <strong class="affirm-text">affirm</strong>
                <svg class="affirm-arc-svg" width="20" height="14" viewBox="0 0 24 14" fill="none">
                  <path d="M2 12C7 2 17 2 22 12" stroke="#4A4EFE" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modalOverlay);

    const closeBtn = document.getElementById('shoppayModalClose');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeShopPayModal);
    }

    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        closeShopPayModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalOverlay.classList.contains('active')) {
        closeShopPayModal();
      }
    });
  }

  function openShopPayModal(e) {
    if (e) e.preventDefault();
    createShopPayModal();
    const overlay = document.getElementById('shoppayModalOverlay');
    if (overlay) {
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeShopPayModal() {
    const overlay = document.getElementById('shoppayModalOverlay');
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  window.openShopPayModal = openShopPayModal;
  window.closeShopPayModal = closeShopPayModal;

  // Global event delegation for all "Learn More" links inside product-info-shoppay
  document.addEventListener('click', (e) => {
    const target = e.target.closest('.shoppay-learn-more, .product-info-shoppay a, [data-shoppay-modal]');
    if (target) {
      e.preventDefault();
      openShopPayModal(e);
    }
  });
}

// Initialize YouTube Episodes Carousels (Home & YouTube Page)
const initYouTubeCarousel = () => {
  const configs = [
    { gridId: 'youtubeHomeGrid', prevId: 'ytHomePrev', nextId: 'ytHomeNext' },
    { gridId: 'youtubePageGrid', prevId: 'ytPagePrev', nextId: 'ytPageNext' }
  ];

  configs.forEach(({ gridId, prevId, nextId }) => {
    const grid = document.getElementById(gridId);
    const prevBtn = document.getElementById(prevId);
    const nextBtn = document.getElementById(nextId);

    if (!grid) return;

    const getScrollStep = () => {
      const card = grid.querySelector('.youtube-card, .youtube-page-card');
      if (!card) return 339;
      const gap = parseInt(window.getComputedStyle(grid).gap || '24') || 24;
      return card.offsetWidth + gap;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const step = getScrollStep();
        grid.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const step = getScrollStep();
        grid.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    // Mouse drag-to-scroll support
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    grid.addEventListener('mousedown', (e) => {
      isDown = true;
      grid.style.cursor = 'grabbing';
      grid.style.scrollBehavior = 'auto';
      grid.style.scrollSnapType = 'none';
      startX = e.pageX - grid.offsetLeft;
      scrollLeft = grid.scrollLeft;
    });

    const stopDragging = () => {
      if (!isDown) return;
      isDown = false;
      grid.style.cursor = '';
      grid.style.scrollBehavior = 'smooth';
      grid.style.scrollSnapType = 'x mandatory';
    };

    grid.addEventListener('mouseleave', stopDragging);
    grid.addEventListener('mouseup', stopDragging);

    grid.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - grid.offsetLeft;
      const walk = (x - startX) * 1.5;
      grid.scrollLeft = scrollLeft - walk;
    });
  });
};

// Infinite Reviews Continuous Slider for Product Pages
const initReviewsSlider = () => {
  const track = document.getElementById('reviewsTrack');
  const outer = document.querySelector('.reviews-carousel-outer');
  if (!track || !outer) return;
  if (track.dataset.reviewsInitialized === 'true') return;
  track.dataset.reviewsInitialized = 'true';

  const isMobile = () => window.innerWidth <= 992;
  const originalCards = Array.from(track.children);
  if (originalCards.length === 0) return;

  // Clone twice for seamless continuous infinite loop
  originalCards.forEach(card => track.appendChild(card.cloneNode(true)));
  originalCards.forEach(card => track.appendChild(card.cloneNode(true)));

  let speed = 0.6;
  let pos = 0;
  let isHovered = false;
  let isDragging = false;
  let startX = 0;
  let dragStartPos = 0;

  function getSingleSetWidth() {
    let w = 0;
    const gap = 20;
    for (let i = 0; i < originalCards.length; i++) {
      w += originalCards[i].offsetWidth + gap;
    }
    return w;
  }

  function step() {
    if (!isMobile()) {
      const singleWidth = getSingleSetWidth();
      if (singleWidth > 0) {
        if (!isHovered && !isDragging) {
          pos += speed;
          if (pos >= singleWidth) pos = 0;
          track.style.transform = `translateX(-${pos}px)`;
        }
      }
    }
    requestAnimationFrame(step);
  }

  requestAnimationFrame(step);

  outer.addEventListener('mouseenter', () => { if (!isMobile()) isHovered = true; });
  outer.addEventListener('mouseleave', () => { if (!isMobile()) isHovered = false; });

  outer.addEventListener('mousedown', (e) => {
    if (isMobile()) return;
    isDragging = true;
    isHovered = true;
    startX = e.pageX;
    dragStartPos = pos;
    outer.style.cursor = 'grabbing';
  });

  window.addEventListener('mousemove', (e) => {
    if (!isDragging || isMobile()) return;
    const delta = e.pageX - startX;
    pos = dragStartPos - delta;
    const singleWidth = getSingleSetWidth();
    if (pos < 0) pos += singleWidth;
    if (pos >= singleWidth) pos -= singleWidth;
    track.style.transform = `translateX(-${pos}px)`;
  });

  window.addEventListener('mouseup', () => {
    if (isDragging) {
      isDragging = false;
      outer.style.cursor = 'grab';
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initShopPayModal();
    initYouTubeCarousel();
    initReviewsSlider();
  });
} else {
  initShopPayModal();
  initYouTubeCarousel();
  initReviewsSlider();
}
